import {useContext, useEffect, useRef, useState} from "react";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {CareJourneyDetailsResponse} from "@/api/dto/care-journey";
import SlideInPane from "patient-ping-remedy/packages/slide-in-pane";
import FormPDFViewer from "@clients/profile_page/forms_tab/FormPDFViewer";
import {useMutation} from "@tanstack/react-query";
import FileHelpers from "@/helpers/file_helpers";
import {AxiosError} from "axios";
import Helpers from "@/helpers/helpers";
import Loading from "@common/Loading";

type Props = {
    careJourneyDetails: CareJourneyDetailsResponse
    isOpen: boolean;
    closeSlide: () => void;
}

export default function ReferralDocumentPreview({careJourneyDetails, closeSlide, isOpen}: Props) {
  const {carecoApi} = useContext(CarecoApiContext);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  // Raw blob URL kept for revocation; pdfUrl holds the display URL (with #toolbar=0).
  const objectUrlRef = useRef<string | null>(null);
  // referralId of the most recent request — responses for any other (outdated) referral are ignored
  // so a late-resolving fetch can't overwrite the current preview (CARECO-4583).
  const latestReferralIdRef = useRef<string | null>(null);

  const {mutate: fetchDoc, isPending} = useMutation({
    mutationFn: async (referralId: string) => carecoApi?.getCareJourneyReferralPreview(referralId),
    onSuccess: (response, referralId) => {
      if (referralId !== latestReferralIdRef.current || !response?.base64Content) return;
      const buffer = FileHelpers.base64ToArrayBuffer(response.base64Content);
      const blob = new Blob([buffer], {type: 'application/pdf'});
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;
      setPdfUrl(objectUrl + '#toolbar=0');
    },
    onError: (error: AxiosError) => Helpers.alertOnError(`Failed to load referral document. ${Helpers.traceId(error)}`, error)
  });

  useEffect(() => {
    const referralId = careJourneyDetails.referralId;
    if (!isOpen || !referralId) return;
    // Drop any prior preview so an outdated PDF isn't shown while the new one loads, and revoke it.
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setPdfUrl('');
    latestReferralIdRef.current = referralId;
    fetchDoc(referralId);
  }, [isOpen, careJourneyDetails.referralId]);

  const close = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setPdfUrl('');
    closeSlide();
  };

  return (
    <>
      <SlideInPane
        isOpen={isOpen}
        onClose={close}
        showNav={false}
        headerText={"Referral document"}>
        {isPending && <Loading/>}
        {!isPending && pdfUrl && <FormPDFViewer id="referral-preview-pdf-viewer" url={pdfUrl}/>}
        {!isPending && !pdfUrl && <div>No referral document available.</div>}
      </SlideInPane>
    </>
  );
}
